import { useEffect, useRef, useState } from 'react';
import { useEditorStore } from '@/store/editorStore';
import type { QueryResult } from '@/types/domain';
import './ResultsGrid.css';

function isQueryResult(x: unknown): x is QueryResult {
  return typeof x === 'object' && x !== null && 'columns' in x && 'rows' in x;
}

export function ResultsGrid() {
  const resp        = useEditorStore(s => s.lastResponse);
  const isExecuting = useEditorStore(s => s.isExecuting);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (isExecuting) {
      setElapsed(0);
      intervalRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isExecuting]);

  if (isExecuting) {
    return (
      <div className="results">
        <div className="results-executing">
          <span className="results-executing-spinner" />
          <span>Executando consulta<span className="results-executing-dots" />  —  {elapsed}s</span>
        </div>
      </div>
    );
  }

  if (!resp) {
    return (
      <div className="results">
        <div className="results-empty">
          Execute uma query para ver o resultado.
        </div>
      </div>
    );
  }

  if (!isQueryResult(resp)) {
    return (
      <div className="results">
        <div className={`results-banner ${resp.status === 'denied' ? 'deny' : 'warn'}`}>
          <b>{resp.status === 'denied' ? 'Execução negada' : 'Aprovação necessária'}</b>
          {resp.status === 'denied' && <span> — {resp.reason}</span>}
          <ul>
            {resp.rules.map(r => (
              <li key={r.ruleId}><code>{r.ruleId}</code> · {r.action} · {r.reason}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  const { columns, rows, maskedColumns, durationMs, status, errorMessage } = resp;
  const maskedNames = Object.keys(maskedColumns);

  return (
    <div className="results">
      {status === 'Error' && errorMessage && (
        <div className="results-banner deny">
          <b>Erro</b> — {errorMessage}
        </div>
      )}
      <div className="results-header">
        <div className="results-tab active">Resultado #1</div>
        <div className="results-tab">Mensagens</div>
        <div className="results-tab">Plano de execução</div>
        <div className="results-stats">
          <span><b>{rows.length}</b> linhas</span>
          <span><b>{(durationMs / 1000).toFixed(2)} s</b></span>
          {maskedNames.length > 0 && (
            <span className="warn">⚠ <b>{maskedNames.join(', ')}</b> mascarado por política</span>
          )}
        </div>
      </div>
      <div className="grid-wrapper">
        <table className="grid">
          <thead>
            <tr>
              <th className="row-idx">#</th>
              {columns.map(c => (
                <th key={c.name}>
                  {c.name}
                  {c.masked && <span className="mask-indicator"> 🔒</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td className="row-idx">{i + 1}</td>
                {row.map((cell, j) => {
                  const col = columns[j];
                  if (cell === null) return <td key={j} className="null-cell">NULL</td>;
                  if (col?.masked) return <td key={j} className="mask-cell">{String(cell)}</td>;
                  if (typeof cell === 'number') return <td key={j} className="num-cell">{cell}</td>;
                  return <td key={j} className="str-cell">{String(cell)}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
